/**
 * foreignObject — primary text serialization strategy.
 *
 * Embeds the LIVE element's full HTML subtree inside an <svg><foreignObject>
 * so the browser's HTML text engine handles layout end-to-end. Nothing is
 * recomputed or reconstructed — the browser-laid-out DOM IS what gets
 * rasterized.
 *
 * Critical correctness rule: **clone the entire inner subtree, not just
 * textContent.** CanvasEditor's text branch wraps the user text in a
 * three-level structure (flex container → padding wrapper → pre-wrap
 * inner div). Dropping any of those changes the layout context — padding
 * disappears, white-space collapses, line height shifts. Past versions
 * only copied `textContent` and that's the source of the "EXCLUSIVELYOFFERED"
 * / "4BATHROOMS" word-collapse bug.
 *
 * Returns true on success. On failure, pushes a structured failure record
 * and returns false — caller can decide whether to fall back to svg-word.
 */

import { getEmbeddedFontCssFor } from './exportFontEmbed';

const SVG_NS   = 'http://www.w3.org/2000/svg';
const XHTML_NS = 'http://www.w3.org/1999/xhtml';

// Text-affecting properties copied from the live element's computed style
// onto the inner xhtml div. Inheritance from the host div in the cloned
// canvas may differ from the live tree (computed-style context differences
// inside foreignObject), so we pin them explicitly. NOTE: we copy from the
// LIVE element so the rendered values inherit correctly into the cloned
// subtree.
const COPIED_TEXT_STYLES = [
  'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontVariant',
  'fontVariantLigatures', 'fontVariationSettings',
  'lineHeight', 'letterSpacing', 'wordSpacing',
  'color', 'textAlign', 'textDecoration', 'textTransform',
  'whiteSpace', 'wordBreak', 'overflowWrap',
  'fontKerning', 'fontFeatureSettings',
  'writingMode', 'direction', 'unicodeBidi',
  'textShadow',
  // The stroke of outlined type is set on the HOST element, which is not part
  // of the cloned subtree. Without these an outlined word (transparent fill,
  // coloured stroke) is exported as transparent text with no stroke at all —
  // it simply disappears from the download — and filled type with an outline
  // loses the outline.
  'WebkitTextStrokeWidth', 'WebkitTextStrokeColor', 'WebkitTextFillColor', 'paintOrder',
];

// 'fontFamily' → 'font-family'; 'WebkitTextStrokeWidth' → '-webkit-text-stroke-width'.
const cssName = (camel) =>
  camel.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase());

// A property's computed value, tolerating engines (and jsdom) that expose a
// vendor-prefixed property only under its lower-case name or its CSS name.
const computedValue = (cs, prop) => {
  const direct = cs[prop];
  if (direct) return direct;
  const lower = prop.charAt(0).toLowerCase() + prop.slice(1);
  if (cs[lower]) return cs[lower];
  return typeof cs.getPropertyValue === 'function' ? cs.getPropertyValue(cssName(prop)) : '';
};

/**
 * Verify the cloned subtree has the same source text as the live element.
 * Catches any serialization corruption (whitespace mangled, characters
 * dropped, content reordered). Returns true when they match.
 */
function verifyTextContentParity(liveEl, clonedSubtreeRoot) {
  const live = (liveEl.textContent || '').replace(/\s+/g, ' ').trim();
  const clone = (clonedSubtreeRoot.textContent || '').replace(/\s+/g, ' ').trim();
  return live === clone;
}

export function serializeTextAsForeignObject(divEl, liveEl, failures) {
  if (!liveEl) {
    failures.push({
      reason: 'no live element for foreignObject path',
      info: { id: divEl.getAttribute('data-export-id') },
    });
    return false;
  }

  const cs = window.getComputedStyle(liveEl);
  const w = parseFloat(divEl.style.width)  || liveEl.offsetWidth  || 0;
  const h = parseFloat(divEl.style.height) || liveEl.offsetHeight || 0;
  if (w <= 0 || h <= 0) return false;

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('xmlns', SVG_NS);
  svg.setAttribute('width',  String(w));
  svg.setAttribute('height', String(h));
  svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  // overflow=visible at root so a descender doesn't get clipped to the
  // viewBox — important for fonts whose `g`/`p`/`y` reach below the baseline.
  svg.setAttribute('overflow', 'visible');
  svg.style.position = 'absolute';
  svg.style.left = '0';
  svg.style.top  = '0';
  svg.style.display  = 'block';
  svg.style.overflow = 'visible';
  // Determinism: pin font smoothing so rasterization doesn't depend on
  // OS subpixel defaults that vary between dev and CI environments.
  svg.style.webkitFontSmoothing = 'antialiased';
  svg.style.mozOsxFontSmoothing = 'grayscale';

  const fo = document.createElementNS(SVG_NS, 'foreignObject');
  fo.setAttribute('x', '0');
  fo.setAttribute('y', '0');
  fo.setAttribute('width',  String(w));
  fo.setAttribute('height', String(h));
  fo.setAttribute('overflow', 'visible');

  // Inner XHTML root. The browser knows to treat its descendants as
  // HTML (not SVG) once the namespace is XHTML.
  const inner = document.createElementNS(XHTML_NS, 'div');
  inner.setAttribute('xmlns', XHTML_NS);
  inner.style.width  = '100%';
  inner.style.height = '100%';
  inner.style.margin  = '0';
  inner.style.padding = '0';
  inner.style.boxSizing = 'border-box';
  // Pin text-affecting styles from the live element's computed style.
  // The inner DOM subtree we clone in below has its own inline styles
  // already; this layer establishes the inheritance baseline that the
  // host div would normally provide.
  // A style object may refuse a property it does not model (a vendor-prefixed
  // one, in some engines): those are written into the style attribute text
  // after the loop, so a later setProperty cannot re-serialize them away.
  const unmodelled = [];
  for (const prop of COPIED_TEXT_STYLES) {
    const val = computedValue(cs, prop);
    if (val && val !== 'normal' && val !== 'auto' && val !== 'none') {
      const name = cssName(prop);
      inner.style.setProperty(name, val);
      if (!inner.style.getPropertyValue(name)) unmodelled.push(`${name}: ${val}`);
    }
  }
  if (unmodelled.length) {
    inner.setAttribute('style', `${inner.getAttribute('style') || ''} ${unmodelled.join('; ')};`);
  }

  // Clone the ENTIRE live subtree — flex wrapper, padding wrapper,
  // pre-wrap inner div, text node. innerHTML preserves all inline
  // styles and DOM structure. The host element's transform / clip-path
  // / opacity stay on the outer host div (untouched) where html2canvas
  // reads them directly.
  inner.innerHTML = liveEl.innerHTML;

  // Invariant: cloned subtree text content must match the live element
  // (mod collapsed whitespace). If it doesn't, serialization got the
  // wrong DOM — fail loudly so the caller falls back to svg-word.
  if (!verifyTextContentParity(liveEl, inner)) {
    failures.push({
      reason: 'foreignObject-text-content-mismatch',
      info: {
        id: divEl.getAttribute('data-export-id'),
        live:  (liveEl.textContent || '').slice(0, 200),
        clone: (inner.textContent  || '').slice(0, 200),
      },
    });
    return false;
  }

  // Embed the page's @font-face rules INSIDE the foreignObject's HTML so the
  // content resolves its real fonts when html2canvas rasterizes this SVG as a
  // data-URL image — a null-origin context with no access to the parent
  // document's <link> stylesheets. The rules MUST live inside the HTML subtree:
  // an SVG-root <style> does NOT reliably cascade into foreignObject HTML when
  // the SVG is rendered as an image, so custom/script fonts otherwise fall back
  // to a default face (and their wider metrics overflow + clip the text).
  // Binaries are already base64 data: URLs, so no network fetch is needed at
  // rasterization time. Injected AFTER the parity check so the <style>'s text
  // content doesn't skew the text-content comparison above.
  // Only this element's own family/weight/style — NOT the whole embedded
  // catalog. Injecting the full CSS into every foreignObject made the export
  // serialize tens of MB and crawl.
  const fontCss = getEmbeddedFontCssFor(cs.fontFamily, cs.fontWeight, cs.fontStyle);
  if (fontCss) {
    const styleEl = document.createElementNS(XHTML_NS, 'style');
    styleEl.textContent = fontCss;
    inner.insertBefore(styleEl, inner.firstChild);
  }

  fo.appendChild(inner);
  svg.appendChild(fo);

  while (divEl.firstChild) divEl.removeChild(divEl.firstChild);
  divEl.appendChild(svg);
  // Host div's text-affecting properties are now overridden by the inner
  // div's pinned values; neutralize the host's color so nothing leaks
  // beneath the foreignObject content.
  divEl.style.color = 'transparent';
  return true;
}

/**
 * Whitespace diagnostic snapshot for a text node. Called when serialization
 * fails or when post-export diff exceeds threshold so the failure log
 * carries enough context to reproduce.
 */
export function whitespaceDiagnostic(liveEl) {
  if (!liveEl) return null;
  const cs = window.getComputedStyle(liveEl);
  const raw = liveEl.textContent || '';
  return {
    id: liveEl.getAttribute('data-export-id'),
    rawLength: raw.length,
    visibleLength: raw.replace(/\s+/g, ' ').trim().length,
    consecutiveSpaces: (raw.match(/ {2,}/g) || []).length,
    explicitLineBreaks: (raw.match(/\n/g) || []).length,
    leadingWhitespace: /^\s/.test(raw),
    trailingWhitespace: /\s$/.test(raw),
    whiteSpaceMode: cs.whiteSpace,
    wordBreak: cs.wordBreak,
    overflowWrap: cs.overflowWrap,
    wordSpacing: cs.wordSpacing,
    letterSpacing: cs.letterSpacing,
    direction: cs.direction,
    sample: JSON.stringify(raw.slice(0, 80)),
  };
}
